import React, { useState } from "react";
import Footer from "./components/Footer";
import Header from "./components/Header";

export default function AboutPage() {
  const [showGT, setShowGT] = useState(false);
  const [showKSU, setShowKSU] = useState(false);
  const [showUTC, setShowUTC] = useState(false);

  return (
    <main className="bg-zinc-900 text-zinc-100 px-6 md:px-12 py-12 space-y-2 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 w-full text-center space-y-4">
          <Header />
        </div>
          {/* Profile Section */}
          <section className="max-w-6xl mx-auto text-zinc-100 text-left">
            <h2 className="text-2xl font-semibold border-t border-zinc-700 pt-8 mt-8">Welcome</h2>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-x-6 gap-y-1">
              <ul className="space-y-6 text-zinc-300">
                <div className="md:col-span-2 space-y-2 text-zinc-300 border-t border-zinc-700 pt-8 mt-8">
                  <div>
                    <strong>Some useful information about me: </strong>
                  </div>
                  <ul className="list-disc list-outside pl-6 space-y-2 text-zinc-300">
                    <li>Graduated with a Master’s in Computer Science from Georgia Tech in May 2025, specializing in Interactive Intelligence.</li>
                  <ul className="list-none list-outside pl-6 space-y-2 italic mt-1 text-zinc-300">
                    <li>
                      (Georgia Tech is ranked sixth nationally for graduate AI and CS by{" "}
                      <a
                        href="https://www.usnews.com/best-graduate-schools/georgia-institute-of-technology-139755/overall-rankings"
                        className="underline text-green-700"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        U.S. News & World Report
                      </a>).
                    </li>
                  </ul>
                    <li>Complemented software development and AI coursework with training in statistics, business processes, and data visualization to prepare for analyst roles.</li>
                    <li>Completed some graduate level coursework at the University of Tennessee at Chattanooga, earned a Software Engineering certificate from Kennesaw State University, and graduated cum laude with a BS in Psychology from UTC.</li>
                  </ul>
                </div>
              </ul>
            </div>
          </section>

          <section className="max-w-6xl mx-auto text-zinc-100 text-left">
            <h2 className="text-2xl font-semibold border-t border-zinc-700 pt-8 mt-8">Professional Experience</h2>
            <ul className="space-y-6 text-zinc-300">
              <li className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-x-6 gap-y-1 space-y-2 border-t border-zinc-700 pt-8 mt-8">
                <div>
                  <strong>Chief Executive Officer and Founder</strong>, Auteuristic Systems, Inc.
                </div>
                <div className="text-sm text-zinc-400 whitespace-nowrap self-start md:text-right ">
                  (July 2025 – Present, Chattanooga, TN)
                </div>
                <div className="md:col-span-2 text-zinc-300">
                  <ul className="list-disc list-outside pl-6 space-y-2 text-zinc-300">
                    <li>Founded and lead a neurodivergent-driven assistive technology company developing privacy-first AI solutions for accessibility, communication, and personalized agent experiences.</li>
                    <li>Direct all aspects of company strategy, R&D, and product development, with a mission to create scalable AI technologies centered on real user needs.</li>
                    <li>Oversee executive functions: corporate registration, product vision, team leadership, business development, and partnership formation.</li>
                    <li>Committed to advancing equity and innovation in human-AI interaction, with an emphasis on privacy and usability for underserved communities.</li>
                  </ul>
                </div>
              </li>

              <li className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-x-6 gap-y-1 space-y-2 border-t border-zinc-700 pt-8 mt-8">
                <div>
                  <strong>Artificial Intelligence Trainer</strong>, DataAnnotation.Tech
                </div>
                <div className="text-sm text-zinc-400 whitespace-nowrap self-start md:text-right ">
                  (August 2023 – Present, Remote)
                </div>
                <div className="md:col-span-2 text-zinc-300">
                  <ul className="list-disc list-outside pl-6 space-y-2 text-zinc-300">
                    <li>Utilize reinforcement learning from human feedback (RLHF) techniques to evaluate and improve AI model behavior.</li>
                    <li>Focus areas include programming and code analysis using Python, JavaScript, TypeScript, Java, and various SQL dialects (including Redshift and SQLite).</li>
                    <li>Regularly use development tools such as <strong>VS Code</strong>, <strong>Visual Studio 2022</strong>, <strong>PyCharm</strong>, <strong>R</strong>, and <strong>GitHub</strong>, along with <strong>Docker</strong> for containerized environments.</li>
                    <li>Also experienced with full-stack technologies including <strong>React</strong>, <strong>Tailwind CSS</strong>, and <strong>npm</strong> as part of frontend and backend evaluation workflows.</li>
                  </ul>
                </div>
              </li>

              <li className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-x-6 gap-y-1 space-y-2 border-t border-zinc-700 pt-8 mt-8">
                <div>
                  <strong>Software Engineer Intern</strong>, Grid Protection Alliance
                </div>
                <div className="text-sm text-zinc-400 whitespace-nowrap self-start md:text-right">
                  (June – July 2023, Chattanooga, TN)
                </div>
                <div className="md:col-span-2 text-zinc-300">
                  <ul className="list-disc list-outside pl-6 space-y-2 text-zinc-300">
                    <li>Developed TypeScript + React components integrated into the Tennessee Valley Authority’s (TVA) live production system.</li>
                    <li>Actively contributed to{' '}
                    <a
                      href="https://github.com/GridProtectionAlliance/SEBrowser"
                      className="underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      SEBrowser
                    </a>
                    , an open-source single-line diagram visualization tool used by electric co-ops to monitor and explore real-time grid models and synchrophasor data.</li>
                  </ul>
                </div>
              </li>

              <li className="grid grid-cols-1 md:grid-cols-[1fr_auto] space-y-2 gap-x-6 gap-y-1 border-t border-zinc-700 pt-8 mt-8">
                <div>
                  <strong>Graduate Teaching Assistant</strong>, University of Tennessee at Chattanooga
                </div>
                <div className="text-sm text-zinc-400 whitespace-nowrap self-start md:text-right">
                  (August 2022 – May 2023, Chattanooga, TN)
                </div>
                <div className="md:col-span-2 text-zinc-300">
                  <ul className="list-disc list-outside pl-6 space-y-2 text-zinc-300">
                    <li>Led undergraduate lab sessions for <strong>CS 1100 (Fundamentals of Computer Science)</strong> and <strong>CS 1110 (Data Structures and Program Design)</strong>, covering topics such as control structures, recursion, arrays, and object-oriented design in Java.</li>
                    <li>Provided one-on-one and group tutoring, assisted with debugging assignments, and guided students through conceptual problem-solving.</li> 
                    <li>Helped grade assignments.</li>
                  </ul>
                </div>
              </li>

              <li className="grid grid-cols-1 md:grid-cols-[1fr_auto] space-y-2 gap-x-6 gap-y-1 border-t border-zinc-700 pt-8 mt-8">
                <div>
                  <strong>Tutor</strong>, iD Tech Camps
                </div>
                <div className="text-sm text-zinc-400 whitespace-nowrap self-start md:text-right">
                  (Summer 2022, Remote)
                </div>
                <div className="md:col-span-2 text-zinc-300">
                  <ul className="list-disc list-outside pl-6 space-y-2 text-zinc-300">
                    <li>Delivered personalized, online tutoring to middle and high school students in <strong>Java</strong>, <strong>JavaScript</strong>, and <strong>front-end design</strong>.</li>
                    <li>Developed lesson plans, and provided interactive project guidance in real-time via remote learning platforms.</li>
                  </ul>
                </div>
              </li>
            </ul>
          </section>


          {/* Education */}
            <section className="max-w-6xl mx-auto text-zinc-100 text-left">
              <h2 className="text-2xl font-semibold border-t border-zinc-700 pt-8 mt-8">Education & Achievements</h2>
              <div>
                <p className="font-semibold max-w-6xl mx-auto space-y-6 border-t border-zinc-700 pt-8 mt-8">Georgia Tech – M.S. in Computer Science (2025)</p>
                <button onClick={() => setShowGT(!showGT)} className="underline mt-1">
                  {showGT ? "Hide courses" : "Click here for courses taken"}
                </button>
                {showGT && (
                  <ul className="list-disc list-outside pl-6 space-y-2 text-zinc-300">
                    <li>CS 7638 Robotics: AI Techniques</li>
                    <li>CS 7637 Knowledge-Based AI</li>
                    <li>CS 7632 Game AI</li>
                    <li>CS 6795 Introduction to Cognitive Science</li>
                    <li>CS 6603 AI, Ethics, and Society</li>
                    <li>CS 6601 Artificial Intelligence</li>
                    <li>CS 6457 Video Game Design</li>
                    <li>CS 6300 Software Development Process</li>
                    <li>ISYE 6501 Introduction to Analytics Modeling</li>
                    <li>MGT 8813 Financial Modeling</li>
                  </ul>
                )}
              </div>
              <div>
                <p className="font-semibold space-y-2 gap-x-6 gap-y-1 border-t border-zinc-700 pt-8 mt-8">Kennesaw State – Graduate Certificate in Software Engineering (2022)</p>
                <button onClick={() => setShowKSU(!showKSU)} className="underline mt-1">
                  {showKSU ? "Hide courses" : "Click here for courses taken"}
                </button>
                {showKSU && (
                  <ul className="list-disc list-outside pl-6 space-y-2 text-zinc-300">
                    <li>SWE 5063 Foundations of Databases and Web</li>
                    <li>SWE 5003 SWE and Computational Thinking</li>
                    <li>CS 5040 Data Structures and Algorithms</li>
                    <li>CS 5000 Foundations of Programming</li>
                  </ul>
                )}
              </div>
              <div>
                <p className="font-semibold space-y-2 gap-x-6 gap-y-1 border-t border-zinc-700 pt-8 mt-8">University of Tennessee at Chattanooga – B.S. in Psychology, Cum Laude (2020)</p>
                <button onClick={() => setShowUTC(!showUTC)} className="underline mt-1">
                  {showUTC ? "Hide courses" : "Click here for courses taken"}
                </button>
                {showUTC && (
                  <ul className="list-disc list-outside pl-6 space-y-2 text-zinc-300">
                    <li>CPSC 5530 Data Visualization and Exploration</li>
                    <li>CPSC 5240 Principles of Data Analytics</li>
                    <li>CPSC 5020 Computing Systems</li>
                    <li>CPSC 1100 Fundamentals of Computer Science</li>
                    <li>CPSC 1000 Introduction to Computing</li>
                    <li>PSY 4600 Systems of Psychology</li>
                    <li>PSY 4480 Theories of Personality</li>
                    <li>PSY 4250 Psychology and Law</li>
                    <li>PSY 4230 Psychology of Aging</li>
                    <li>PSY 4210 Psychology of Child Development</li>
                    <li>PSY 3570R Practicum in Teaching Psychology</li>
                    <li>PSY 3310 Social Psychology</li>
                    <li>PSY 3140 Biological Psychology</li>
                    <li>PSY 3130 Cognitive Science</li>
                    <li>PSY 3070 Psychology as a Profession</li>
                    <li>PSY 3040 Statistics in Psychology Lab</li>
                    <li>PSY 3040 Statistics in Psychology</li>
                    <li>PSY 3010 Research Methods</li>
                    <li>PSY 2230 Psychology of Aging</li>
                    <li>PSY 2020 Research Methods: Lab & Field</li>
                    <li>PSY 2010 Research Methodology</li>
                    <li>PSY 1010 Introduction to Psychology</li>
                    <li>MKT 3130 Principles of Marketing</li>
                    <li>MGT 4380 International Management</li>
                    <li>MGT 3150 Management Concepts, Theory, and Practice</li>
                    <li>MGT 3120 Statistical Methods for Business II</li>
                    <li>MGT 3110 Operations Management</li>
                    <li>MATH 2100 Introductory Statistics</li>
                    <li>MATH 2030 Discrete Mathematics for Computer Science</li>
                    <li>MATH 1950 Calculus with Analytic Geometry I</li>
                    <li>MATH 1830 Calculus for Management, Life, and Social Sciences</li>
                    <li>MATH 1730 Combined Precalculus</li>
                    <li>MLNG 1500 World Cinema</li>
                    <li>FIN 3020 Essentials of Managerial Finance</li>
                    <li>ESC 1100 Conservation of Biodiversity</li>
                    <li>ENGL 2880 Professional Writing</li>
                    <li>ENGL 1150 Western Humanities II</li>
                    <li>ECON 3240 Intermediate Microeconomic Theory</li>
                    <li>ECON 1020 Microeconomics</li>
                    <li>ECON 1010 Macroeconomics</li>
                    <li>CLAS 1200 Legendary Rome</li>
                    <li>CRMJ 1100 Introduction to Criminal Justice</li>
                    <li>BIOL 1110 Principles of Biology I</li>
                    <li>ACC 4070 Governmental Accounting</li>
                    <li>ACC 3050 Managerial Cost Accounting</li>
                    <li>ACC 3010 Intermediate Accounting II</li>
                    <li>ACC 3000 Intermediate Accounting I</li>
                    <li>ACC 2010 Principles of Accounting I</li>
                    <li>REL 2110 Religions of the East</li>
                    <li>MUS 1350 Guitar Class</li>
                  </ul>
                )}
              </div>
            </section>
        <Footer />
      </div>  
    </main>
  );
}
